// ═══════════════════════════════════════════════════════════════════════════════
// DIGITAL TWIN ENGINE v17.0
// EnergiPro Solutions — Smart RFQ System
// ═══════════════════════════════════════════════════════════════════════════════
// Modules: Scenario Builder, Rule-Based Simulation, Risk Profiling,
//          Confidence Scoring, What-If Analysis
// Principle: Decision support only — does NOT replace engineering judgment
// ═══════════════════════════════════════════════════════════════════════════════

import { PortalRFQ } from '../../pages/portal/portalTypes';

// ─── TYPES ───────────────────────────────────────────────────────────────────

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
export type EquipmentCategory = 'pressure-control' | 'separation' | 'safety' | 'sand-management' | 'flow-control' | 'monitoring' | 'chemical' | 'logistics';

export interface Scenario {
  id: string;
  rfqId: string;
  createdAt: string;
  inputs: ScenarioInputs;
}

export interface ScenarioInputs {
  pressure: number;       // PSI
  oilRate: number;        // BOPD
  gasRate: number;        // MMSCFD
  sandProduction: 'none' | 'low' | 'medium' | 'high';
  h2sPresence: boolean;
  environment: string;
  locationType: string;
  serviceType: string;
  serviceModel: string;
  duration: string;
}

export interface EquipmentItem {
  id: string;
  name: string;
  category: EquipmentCategory;
  rating: string;
  required: boolean;      // true = mandatory per rules
  reason: string;
  riskIfRemoved: RiskLevel;
}

export interface RiskFactor {
  id: string;
  category: string;
  description: string;
  level: RiskLevel;
  mitigation: string;
  triggered: boolean;
}

export interface SimulationResult {
  scenario: Scenario;
  equipment: EquipmentItem[];
  risks: RiskFactor[];
  overallRisk: RiskLevel;
  confidenceScore: number;  // 0-100
  confidenceBreakdown: ConfidenceBreakdown;
  warnings: string[];
  compliant: boolean;
}

export interface ConfidenceBreakdown {
  pressureCompliance: number;     // 0-25
  safetyCompliance: number;       // 0-25
  capacityCompliance: number;     // 0-25
  environmentalCompliance: number; // 0-25
}

export interface WhatIfChange {
  type: 'remove-equipment' | 'modify-pressure' | 'modify-rate' | 'toggle-h2s' | 'modify-sand';
  equipmentId?: string;
  newValue?: number | string | boolean;
}

// ─── 1. SCENARIO BUILDER ─────────────────────────────────────────────────────

function generateScenarioId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = 'DT-';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function buildScenario(rfq: PortalRFQ): Scenario {
  return {
    id: generateScenarioId(),
    rfqId: rfq.id,
    createdAt: new Date().toISOString(),
    inputs: {
      pressure: parseInt(rfq.wellheadPressure) || 0,
      oilRate: parseInt(rfq.oilRate) || 0,
      gasRate: parseFloat(rfq.gasRate) || 0,
      sandProduction: (rfq.sandProduction as ScenarioInputs['sandProduction']) || 'none',
      h2sPresence: rfq.h2sPresence === 'yes',
      environment: rfq.environment || 'onshore',
      locationType: rfq.locationType || 'standard',
      serviceType: rfq.service,
      serviceModel: rfq.serviceModel || 'full-service',
      duration: rfq.duration || 'short-term',
    },
  };
}

export function buildScenarioFromInputs(inputs: ScenarioInputs, rfqId: string): Scenario {
  return {
    id: generateScenarioId(),
    rfqId,
    createdAt: new Date().toISOString(),
    inputs,
  };
}

// ─── 2. RULE-BASED SIMULATION ENGINE ─────────────────────────────────────────

// Equipment catalog based on engineering rules
function getRequiredEquipment(inputs: ScenarioInputs): EquipmentItem[] {
  const equipment: EquipmentItem[] = [];
  let idCounter = 1;

  // ── Pressure Rules ──
  if (inputs.pressure > 5000) {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: '10K PSI Rated Wellhead Assembly',
      category: 'pressure-control',
      rating: '10,000 PSI WP',
      required: true,
      reason: `Wellhead pressure ${inputs.pressure} PSI exceeds 5000 PSI threshold`,
      riskIfRemoved: 'critical',
    });
    equipment.push({
      id: `eq-${idCounter++}`,
      name: '10K Choke Manifold',
      category: 'pressure-control',
      rating: '10,000 PSI WP',
      required: true,
      reason: 'Required for high-pressure flow control',
      riskIfRemoved: 'critical',
    });
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'High-Pressure Flowline (10K)',
      category: 'flow-control',
      rating: '10,000 PSI',
      required: true,
      reason: 'High-pressure piping for safe fluid transport',
      riskIfRemoved: 'critical',
    });
  } else if (inputs.pressure > 2000) {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: '5K PSI Wellhead Assembly',
      category: 'pressure-control',
      rating: '5,000 PSI WP',
      required: true,
      reason: `Standard pressure control for ${inputs.pressure} PSI`,
      riskIfRemoved: 'high',
    });
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'Adjustable Choke (5K)',
      category: 'pressure-control',
      rating: '5,000 PSI',
      required: true,
      reason: 'Flow rate control and pressure management',
      riskIfRemoved: 'high',
    });
  } else if (inputs.pressure > 0) {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: '1440 PSI Wellhead Assembly',
      category: 'pressure-control',
      rating: '1,440 PSI WP',
      required: true,
      reason: 'Standard low-pressure control equipment',
      riskIfRemoved: 'medium',
    });
  }

  // ── Sand Production Rules ──
  if (inputs.sandProduction === 'high') {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'Sand Cyclone Separator',
      category: 'sand-management',
      rating: 'High-capacity',
      required: true,
      reason: 'High sand production requires dedicated cyclone separation',
      riskIfRemoved: 'critical',
    });
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'Sand Accumulator Tank',
      category: 'sand-management',
      rating: '50 BBL',
      required: true,
      reason: 'Sand collection and disposal system',
      riskIfRemoved: 'high',
    });
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'Erosion-Resistant Piping',
      category: 'sand-management',
      rating: 'Tungsten Carbide Lined',
      required: true,
      reason: 'Prevent erosion damage from high sand loading',
      riskIfRemoved: 'high',
    });
  } else if (inputs.sandProduction === 'medium') {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'Sand Filter (Inline)',
      category: 'sand-management',
      rating: 'Medium-capacity',
      required: true,
      reason: 'Medium sand production requires inline filtering',
      riskIfRemoved: 'medium',
    });
  }

  // ── H₂S Safety Rules ──
  if (inputs.h2sPresence) {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'H₂S Safety System (NACE MR0175)',
      category: 'safety',
      rating: 'NACE Compliant',
      required: true,
      reason: 'H₂S presence requires NACE-rated materials and safety systems',
      riskIfRemoved: 'critical',
    });
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'H₂S Gas Detectors (Fixed + Portable)',
      category: 'safety',
      rating: '0-100 ppm range',
      required: true,
      reason: 'Continuous H₂S monitoring mandatory for personnel safety',
      riskIfRemoved: 'critical',
    });
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'SCBA Units & Wind Socks',
      category: 'safety',
      rating: '30-min rated',
      required: true,
      reason: 'Emergency evacuation equipment for sour gas environment',
      riskIfRemoved: 'critical',
    });
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'H₂S Scavenger Injection System',
      category: 'chemical',
      rating: 'Variable rate',
      required: false,
      reason: 'Optional chemical treatment to reduce H₂S levels',
      riskIfRemoved: 'medium',
    });
  }

  // ── Capacity/Flow Rules ──
  if (inputs.oilRate > 10000) {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'High-Capacity Test Separator',
      category: 'separation',
      rating: '15,000 BOPD',
      required: true,
      reason: `Oil rate ${inputs.oilRate} BOPD requires high-capacity separation`,
      riskIfRemoved: 'high',
    });
  } else if (inputs.oilRate > 5000) {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'Standard Test Separator',
      category: 'separation',
      rating: '10,000 BOPD',
      required: true,
      reason: 'Standard separation for medium oil rates',
      riskIfRemoved: 'high',
    });
  } else if (inputs.oilRate > 0) {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'Compact Test Separator',
      category: 'separation',
      rating: '5,000 BOPD',
      required: true,
      reason: 'Compact separation for low-moderate oil rates',
      riskIfRemoved: 'medium',
    });
  }

  if (inputs.gasRate > 25) {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'High-Rate Gas Flare System',
      category: 'safety',
      rating: '50 MMSCFD',
      required: true,
      reason: `Gas rate ${inputs.gasRate} MMSCFD exceeds standard flare capacity`,
      riskIfRemoved: 'critical',
    });
  } else if (inputs.gasRate > 0) {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'Standard Flare System',
      category: 'safety',
      rating: '25 MMSCFD',
      required: true,
      reason: 'Gas flaring required for safe disposal',
      riskIfRemoved: 'high',
    });
  }

  // ── Monitoring (always recommended) ──
  equipment.push({
    id: `eq-${idCounter++}`,
    name: 'Data Acquisition System (DAS)',
    category: 'monitoring',
    rating: 'Real-time',
    required: false,
    reason: 'Real-time pressure, temperature, and flow monitoring',
    riskIfRemoved: 'low',
  });

  // ── Remote/Offshore Logistics ──
  if (inputs.locationType === 'remote' || inputs.locationType === 'desert') {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'Self-Contained Power Generation',
      category: 'logistics',
      rating: '150 KVA',
      required: true,
      reason: 'No grid power available at remote location',
      riskIfRemoved: 'high',
    });
  }

  if (inputs.environment === 'offshore') {
    equipment.push({
      id: `eq-${idCounter++}`,
      name: 'Marine-Grade Equipment Package',
      category: 'logistics',
      rating: 'DNV Certified',
      required: true,
      reason: 'Offshore environment requires marine-certified equipment',
      riskIfRemoved: 'critical',
    });
  }

  return equipment;
}

// ─── RISK PROFILING ──────────────────────────────────────────────────────────

function evaluateRisks(inputs: ScenarioInputs, equipment: EquipmentItem[]): RiskFactor[] {
  const risks: RiskFactor[] = [];
  let riskId = 1;

  // Pressure risk
  if (inputs.pressure > 7500) {
    risks.push({
      id: `risk-${riskId++}`, category: 'Pressure', level: 'critical',
      description: 'Extreme pressure exceeds most standard equipment ratings',
      mitigation: 'Confirm 10K or 15K rated equipment availability. NDT all connections.',
      triggered: true,
    });
  } else if (inputs.pressure > 5000) {
    risks.push({
      id: `risk-${riskId++}`, category: 'Pressure', level: 'high',
      description: 'High pressure requires premium equipment and testing protocols',
      mitigation: 'Use 10K rated equipment. Pressure test all lines to 1.5x WP.',
      triggered: true,
    });
  } else if (inputs.pressure > 2000) {
    risks.push({
      id: `risk-${riskId++}`, category: 'Pressure', level: 'medium',
      description: 'Moderate pressure — standard precautions apply',
      mitigation: 'Standard pressure testing and inspection protocols.',
      triggered: true,
    });
  } else {
    risks.push({
      id: `risk-${riskId++}`, category: 'Pressure', level: 'low',
      description: 'Low pressure — minimal equipment stress',
      mitigation: 'Standard operating procedures sufficient.',
      triggered: false,
    });
  }

  // H2S risk
  if (inputs.h2sPresence) {
    risks.push({
      id: `risk-${riskId++}`, category: 'H₂S / Toxic Gas', level: 'critical',
      description: 'Sour gas environment — lethal exposure risk to personnel',
      mitigation: 'Full NACE compliance, continuous monitoring, emergency response plan, exclusion zones.',
      triggered: true,
    });
  }

  // Sand risk
  if (inputs.sandProduction === 'high') {
    risks.push({
      id: `risk-${riskId++}`, category: 'Sand / Erosion', level: 'high',
      description: 'High sand loading causes accelerated erosion and equipment failure',
      mitigation: 'Cyclone separators, tungsten-lined piping, frequent UT inspection.',
      triggered: true,
    });
  } else if (inputs.sandProduction === 'medium') {
    risks.push({
      id: `risk-${riskId++}`, category: 'Sand / Erosion', level: 'medium',
      description: 'Moderate sand may cause gradual erosion damage',
      mitigation: 'Inline filters, periodic wall thickness checks.',
      triggered: true,
    });
  }

  // Capacity overload risk
  if (inputs.oilRate > 12000) {
    risks.push({
      id: `risk-${riskId++}`, category: 'Capacity Overload', level: 'high',
      description: 'Oil flow rate approaches separator max capacity — risk of carryover',
      mitigation: 'Monitor liquid levels continuously. Consider parallel separator.',
      triggered: true,
    });
  }

  if (inputs.gasRate > 40) {
    risks.push({
      id: `risk-${riskId++}`, category: 'Capacity Overload', level: 'high',
      description: 'Gas rate exceeds standard flare capacity — blowdown risk',
      mitigation: 'Deploy high-rate flare system. Emergency shutdown valve required.',
      triggered: true,
    });
  }

  // Environmental risk
  if (inputs.environment === 'offshore') {
    risks.push({
      id: `risk-${riskId++}`, category: 'Operational Environment', level: 'high',
      description: 'Offshore operations add logistics complexity and emergency response constraints',
      mitigation: 'Marine-grade equipment, standby vessel, weather window planning.',
      triggered: true,
    });
  } else if (inputs.locationType === 'remote' || inputs.locationType === 'desert') {
    risks.push({
      id: `risk-${riskId++}`, category: 'Operational Environment', level: 'medium',
      description: 'Remote location limits support access and emergency response time',
      mitigation: 'Pre-position spares, self-contained utilities, satellite communications.',
      triggered: true,
    });
  }

  return risks;
}

// ─── CONFIDENCE SCORING ──────────────────────────────────────────────────────

function calculateConfidence(inputs: ScenarioInputs, equipment: EquipmentItem[], removedIds: string[] = []): { score: number; breakdown: ConfidenceBreakdown } {
  const activeEquipment = equipment.filter(e => !removedIds.includes(e.id));

  // Pressure compliance (25 pts)
  let pressureCompliance = 25;
  if (inputs.pressure > 5000) {
    const has10K = activeEquipment.some(e => e.name.includes('10K'));
    if (!has10K) pressureCompliance = 5;
  } else if (inputs.pressure > 2000) {
    const has5K = activeEquipment.some(e => e.name.includes('5K') || e.name.includes('10K'));
    if (!has5K) pressureCompliance = 10;
  }

  // Safety compliance (25 pts)
  let safetyCompliance = 25;
  if (inputs.h2sPresence) {
    const hasH2S = activeEquipment.some(e => e.name.includes('H₂S Safety'));
    const hasDetectors = activeEquipment.some(e => e.name.includes('Detector'));
    const hasSCBA = activeEquipment.some(e => e.name.includes('SCBA'));
    if (!hasH2S) safetyCompliance -= 12;
    if (!hasDetectors) safetyCompliance -= 8;
    if (!hasSCBA) safetyCompliance -= 5;
  }
  const hasFlare = activeEquipment.some(e => e.name.includes('Flare'));
  if (inputs.gasRate > 0 && !hasFlare) safetyCompliance -= 10;

  // Capacity compliance (25 pts)
  let capacityCompliance = 25;
  const hasSeparator = activeEquipment.some(e => e.category === 'separation');
  if ((inputs.oilRate > 0 || inputs.gasRate > 0) && !hasSeparator) capacityCompliance -= 15;
  if (inputs.oilRate > 12000) capacityCompliance -= 5;

  // Environmental compliance (25 pts)
  let environmentalCompliance = 25;
  if (inputs.sandProduction === 'high') {
    const hasSandMgmt = activeEquipment.some(e => e.category === 'sand-management');
    if (!hasSandMgmt) environmentalCompliance -= 15;
  }
  if (inputs.locationType === 'remote' || inputs.locationType === 'desert') {
    const hasPower = activeEquipment.some(e => e.name.includes('Power'));
    if (!hasPower) environmentalCompliance -= 8;
  }
  if (inputs.environment === 'offshore') {
    const hasMarine = activeEquipment.some(e => e.name.includes('Marine'));
    if (!hasMarine) environmentalCompliance -= 12;
  }

  const breakdown: ConfidenceBreakdown = {
    pressureCompliance: Math.max(0, pressureCompliance),
    safetyCompliance: Math.max(0, safetyCompliance),
    capacityCompliance: Math.max(0, capacityCompliance),
    environmentalCompliance: Math.max(0, environmentalCompliance),
  };

  const score = breakdown.pressureCompliance + breakdown.safetyCompliance + breakdown.capacityCompliance + breakdown.environmentalCompliance;
  return { score, breakdown };
}

// ─── OVERALL RISK LEVEL ──────────────────────────────────────────────────────

function getOverallRisk(risks: RiskFactor[]): RiskLevel {
  const triggered = risks.filter(r => r.triggered);
  if (triggered.some(r => r.level === 'critical')) return 'critical';
  if (triggered.filter(r => r.level === 'high').length >= 2) return 'critical';
  if (triggered.some(r => r.level === 'high')) return 'high';
  if (triggered.some(r => r.level === 'medium')) return 'medium';
  return 'low';
}

// ─── 3. MAIN SIMULATION FUNCTION ─────────────────────────────────────────────

export function runSimulation(scenario: Scenario, removedEquipmentIds: string[] = []): SimulationResult {
  const equipment = getRequiredEquipment(scenario.inputs);
  const activeEquipment = equipment.filter(e => !removedEquipmentIds.includes(e.id));
  const risks = evaluateRisks(scenario.inputs, activeEquipment);
  const overallRisk = getOverallRisk(risks);
  const { score, breakdown } = calculateConfidence(scenario.inputs, equipment, removedEquipmentIds);

  // Warnings for removed mandatory equipment
  const warnings: string[] = [];
  const removedMandatory = equipment.filter(e => removedEquipmentIds.includes(e.id) && e.required);
  removedMandatory.forEach(e => {
    warnings.push(`⚠ Mandatory equipment removed: ${e.name} — ${e.reason}`);
  });

  // Check if all mandatory equipment is present
  const compliant = removedMandatory.length === 0;

  return {
    scenario,
    equipment,
    risks,
    overallRisk,
    confidenceScore: score,
    confidenceBreakdown: breakdown,
    warnings,
    compliant,
  };
}

// ─── WHAT-IF ANALYSIS ────────────────────────────────────────────────────────

export function runWhatIf(baseScenario: Scenario, changes: WhatIfChange[]): { modified: ScenarioInputs; result: SimulationResult } {
  const modified = { ...baseScenario.inputs };
  const removedIds: string[] = [];

  changes.forEach(change => {
    switch (change.type) {
      case 'modify-pressure':
        modified.pressure = change.newValue as number;
        break;
      case 'modify-rate':
        modified.oilRate = change.newValue as number;
        break;
      case 'toggle-h2s':
        modified.h2sPresence = change.newValue as boolean;
        break;
      case 'modify-sand':
        modified.sandProduction = change.newValue as ScenarioInputs['sandProduction'];
        break;
      case 'remove-equipment':
        if (change.equipmentId) removedIds.push(change.equipmentId);
        break;
    }
  });

  const newScenario = buildScenarioFromInputs(modified, baseScenario.rfqId);
  const result = runSimulation(newScenario, removedIds);
  return { modified, result };
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

export function getRiskColor(level: RiskLevel): { text: string; bg: string; border: string } {
  switch (level) {
    case 'low': return { text: 'text-green-300', bg: 'bg-green-500/10', border: 'border-green-500/30' };
    case 'medium': return { text: 'text-amber-300', bg: 'bg-amber-500/10', border: 'border-amber-500/30' };
    case 'high': return { text: 'text-orange-300', bg: 'bg-orange-500/10', border: 'border-orange-500/30' };
    case 'critical': return { text: 'text-red-300', bg: 'bg-red-500/10', border: 'border-red-500/30' };
  }
}

export function getCategoryIcon(category: EquipmentCategory): string {
  switch (category) {
    case 'pressure-control': return '🔧';
    case 'separation': return '⚗️';
    case 'safety': return '🛡️';
    case 'sand-management': return '🏜️';
    case 'flow-control': return '🔀';
    case 'monitoring': return '📊';
    case 'chemical': return '🧪';
    case 'logistics': return '🚚';
  }
}

export function getCategoryLabel(category: EquipmentCategory): string {
  switch (category) {
    case 'pressure-control': return 'Pressure Control';
    case 'separation': return 'Separation';
    case 'safety': return 'Safety Systems';
    case 'sand-management': return 'Sand Management';
    case 'flow-control': return 'Flow Control';
    case 'monitoring': return 'Monitoring';
    case 'chemical': return 'Chemical Treatment';
    case 'logistics': return 'Logistics & Utilities';
  }
}
